import { formatAddress as formatLocalizedAddress } from 'localized-address-format';

/**
 * Countries where administrative_area_level_2 is more relevant for Province/Region field.
 * By default (~80% of countries), administrative_area_level_1 is more relevant (federal states, provinces).
 * For these exceptional countries (~15%), administrative_area_level_2 is more relevant:
 * - Countries where L1 is purely administrative and L2 is the identity (provinces, departments, counties)
 * - Countries where L2 is vital to disambiguate cities with repeated names
 */
const LEVEL_2_COUNTRIES = [
    // High confidence (90-100%) - L2 IS the real address identity
    'ES', // España: Provincias
    'IT', // Italia: Province
    'FR', // Francia: Départements
    'PT', // Portugal: Distritos
    'PH', // Filipinas: Provinces (L1 is a regional code)
    'IE', // Irlanda: Counties (L1 are historical provinces not used)
    'GB', // Reino Unido: Counties
    'PE', // Perú: Provincias
    'MA', // Marruecos: Provincias
    'HU', // Hungría: Condados (Megye)
    // High probability (70-90%) - L2 very useful for disambiguation
    'MX', // México: Municipio (Critical to disambiguate thousands of homonymous towns)
    'CO', // Colombia: Municipio (Necessary filter below Department)
    'BE', // Bélgica: Provincias
    'GR', // Grecia: Regional Units (Former prefectures)
    'PL', // Polonia: Powiats
    'RO', // Rumanía: Județ
    'VN', // Vietnam: Districts
    'TH', // Tailandia: Amphoe (Districts)
    'ID', // Indonesia: Kabupaten
    'CZ', // República Checa: Okres
    'SK', // Eslovaquia: Okres
];

const STREET_NUMBER_FIRST_ISO = ['FR'];

/**
 * Determines if a country should use administrative_area_level_2 for the Province/Region field.
 *
 * @param {string} countryIso - The ISO 3166-1 alpha-2 country code
 * @returns {boolean} - True if the country should use administrative_area_level_2 for Province/Region
 */
export const shouldUseAdministrativeAreaLevel2 = (countryIso) => {
    if (!countryIso) return false;
    return LEVEL_2_COUNTRIES.includes(countryIso.toUpperCase());
};

export const formatAddressAndStreetNumber = ({ countryIso, streetNumber, address, subpremise }) => {
    const addressLines = [];
    const iso = (countryIso || '').toUpperCase();
    const numberFirst = STREET_NUMBER_FIRST_ISO.includes(iso);
    if (numberFirst && address && streetNumber) {
        if (streetNumber) addressLines.push(streetNumber);
        if (address) addressLines.push(address);
    } else {
        if (address) addressLines.push(address);
        if (streetNumber) addressLines.push(streetNumber);
    }
    if (subpremise) addressLines.push(subpremise);
    return addressLines;
};

export const formatAddress = (address, address2, city, province, cp, country, idCountry) => {
    const AMERICAN_FORMAT_COUNTRIES_IDS = [75, 42, 180, 146];
    const countryCode =
        idCountry && AMERICAN_FORMAT_COUNTRIES_IDS.includes(parseInt(idCountry, 10)) ? 'US' : 'ES';

    const addressLines = [];
    if (address) addressLines.push(address);
    if (address2) addressLines.push(address2);

    const formatted = formatLocalizedAddress({
        postalCountry: countryCode,
        administrativeArea: province || '',
        locality: city || '',
        postalCode: cp || '',
        addressLines,
    }).join(', ');

    return country ? `${formatted}, ${country}` : formatted;
};

export const formatAddressFromGeocoder = (
    address,
    streetNumber,
    postalCode,
    locality,
    state,
    region,
    country,
    countryIso,
    subpremise,
) => {
    address = address && address.trim();
    streetNumber = streetNumber && streetNumber.trim();
    postalCode = postalCode && postalCode.trim();
    locality = locality && locality.trim();
    state = state && state.trim();
    region = region && region.trim();
    country = country && country.trim();

    const addressLines = formatAddressAndStreetNumber({
        countryIso,
        streetNumber,
        address,
        subpremise,
    });

    const useLevel2 = shouldUseAdministrativeAreaLevel2(countryIso);
    const administrativeArea = useLevel2 ? region || state || '' : state || region || '';

    const formatted = formatLocalizedAddress({
        postalCountry: countryIso || 'ES',
        administrativeArea,
        locality: locality || '',
        postalCode: postalCode || '',
        addressLines,
    }).join(', ');

    return country ? `${formatted}, ${country}` : formatted;
};

export const shouldFormatAddressAsAmericanIso = (countryIso) => {
    if (!countryIso) return false;
    return ['us', 'ca', 'gb', 'mx'].includes(countryIso.toLowerCase());
};
